import React, { useState, useEffect } from "react";
import {
  MapPin, Phone, Facebook, Instagram, Menu, X, MessageCircle, Info,
  Stethoscope, ShieldCheck, Smile, CalendarCheck, Eye, CheckCircle2,
  Handshake, Star, Building2, Quote, Clock, Mail,
} from "lucide-react";
import { CLINICS } from "./dentalClinics";
import ConceptCTAPill from "@/components/ConceptCTAPill";

/*
  Shared renderer for every Dental Clinic concept website.
  Each clinic's look, copy and photos come entirely from dentalClinics.js —
  this file only defines the layout and color themes.
*/

const THEMES = {
  sky: { primary: "#0EA5E9", dark: "#0C2D48", light: "#F0F9FF", text: "#0C2D48" },
  mint: { primary: "#16A34A", dark: "#0B3D24", light: "#F0FDF4", text: "#0B3D24" },
  mono: { primary: "#8B7355", dark: "#111111", light: "#FAFAFA", text: "#111111" },
  gold: { primary: "#B8912B", dark: "#241C0B", light: "#FBF6E8", text: "#241C0B" },
  amber: { primary: "#C99B3E", dark: "#3A2E12", light: "#FBF8F1", text: "#3A2E12" },
  slate: { primary: "#5B7C99", dark: "#1E2E3B", light: "#F4F7F9", text: "#1E2E3B" },
  teal: { primary: "#0D9488", dark: "#0F2E2B", light: "#F0FDFA", text: "#0F2E2B" },
  forest: { primary: "#3F7A3F", dark: "#1B3320", light: "#F5FAF3", text: "#1B3320" },
  sage: { primary: "#6E8B5F", dark: "#26301F", light: "#F6F8F2", text: "#26301F" },
  azure: { primary: "#1565A8", dark: "#0A2740", light: "#EFF6FC", text: "#0A2740" },
  taupe: { primary: "#8A7060", dark: "#2E2620", light: "#F7F2EC", text: "#2E2620" },
  crimson: { primary: "#C0122F", dark: "#1A0505", light: "#FFF5F5", text: "#1A0505" },
  navy: { primary: "#2C5F8A", dark: "#0B1E33", light: "#F2F6FA", text: "#0B1E33" },
  rose: { primary: "#B8896F", dark: "#2A1A12", light: "#FBF3EC", text: "#2A1A12" },
};

function Disclaimer({ name }) {
  return (
    <div data-testid="dental-disclaimer" className="bg-black/85 text-white text-center text-xs sm:text-sm px-4 py-2.5">
      <span className="inline-flex items-center gap-2">
        <Info size={14} className="shrink-0 text-white/80" />
        Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {name}.
      </span>
    </div>
  );
}

export default function DentalSite({ slug }) {
  const c = CLINICS[slug];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 10);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  if (!c) return null;
  const t = THEMES[c.theme] || THEMES.sky;
  const links = [["Services", "#services"], ["Reviews", "#reviews"], ["Visit", "#visit"]];
  const heroImg = c.hero;

  return (
    <div id="top" style={{ color: t.text }} className="dental-root min-h-screen pb-16 md:pb-0" data-testid="dental-site-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&family=Manrope:wght@400;500;600;700&display=swap');
        .dental-root{font-family:'Manrope',sans-serif;background:${t.light};}
        .dental-root h1,.dental-root h2,.dental-root h3,.dental-serif{font-family:'Fraunces',serif;}
      `}</style>

      <Disclaimer name={c.name} />

      <header className={`sticky top-0 z-50 transition-all ${scrolled ? "shadow-lg" : ""}`} style={{ background: t.dark }}>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[72px]">
          <a href="#top" data-testid="dental-logo" className="flex items-center gap-3 min-w-0">
            {c.logo ? (
              <img src={c.logo} alt={c.name} className="h-10 w-10 rounded-full object-cover bg-white ring-2" style={{ boxShadow: `0 0 0 2px ${t.primary}` }} />
            ) : (
              <span className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${t.primary}33` }}><Smile size={18} style={{ color: t.primary }} /></span>
            )}
            <span className="font-bold text-white truncate dental-serif text-base sm:text-lg">{c.name}</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/85 hover:text-white transition-colors">{l}</a>)}
            <a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-nav-message" className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:brightness-110 transition" style={{ background: t.primary }}>Message Us</a>
          </div>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="dental-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
        {open && (
          <div className="md:hidden px-4 py-3 space-y-1" style={{ background: t.dark }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block px-2 py-3 text-white/90 font-medium">{l}</a>)}
          </div>
        )}
      </header>

      {/* HERO */}
      {c.heroType === "logo" ? (
        <section className="relative py-16 md:py-24 text-center" style={{ background: t.dark }}>
          <div className="max-w-2xl mx-auto px-4">
            {c.logo && <img src={c.logo} alt={c.name} className="h-28 w-28 mx-auto rounded-2xl object-contain bg-white p-2 shadow-xl" />}
            <h1 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-bold text-white">{c.name}</h1>
            <p className="mt-3 text-base md:text-lg text-white/90">{c.tagline}</p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href="#visit" data-testid="dental-cta-visit" className="rounded-full px-7 py-3 text-sm font-semibold text-white hover:brightness-110 transition" style={{ background: t.primary }}>Visit or Book</a>
              <a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-cta-message" className="rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">Message on Facebook</a>
            </div>
          </div>
        </section>
      ) : c.heroType === "promo" ? (
        <section className="py-10 md:py-16" style={{ background: t.dark }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8 items-center">
            <img src={heroImg} alt={c.name} className="w-full max-h-[220px] md:max-h-[420px] object-contain rounded-2xl shadow-xl mx-auto" loading="lazy" />
            <div className="text-center md:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">{c.name}</h1>
              <p className="mt-3 text-base md:text-lg font-medium text-white/80">{c.tagline}</p>
              <div className="mt-7 flex flex-wrap items-center justify-center md:justify-start gap-3">
                <a href="#visit" data-testid="dental-cta-visit" className="rounded-full px-7 py-3 text-sm font-semibold text-white hover:brightness-110 transition" style={{ background: t.primary }}>Visit or Book</a>
                <a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-cta-message" className="rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">Message on Facebook</a>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="relative">
          <img src={heroImg} alt={c.name} className="w-full h-[42vh] md:h-[58vh] object-cover" style={{ objectPosition: c.heroPosition || "center" }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${t.dark}F2, ${t.dark}CC, ${t.dark}66)` }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
              <div className="max-w-xl">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">{c.name}</h1>
                <p className="mt-3 text-base md:text-lg font-medium text-white/90">{c.tagline}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a href="#visit" data-testid="dental-cta-visit" className="rounded-full px-7 py-3 text-sm font-semibold text-white hover:brightness-110 transition" style={{ background: t.primary }}>Visit or Book</a>
                  <a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-cta-message" className="rounded-full border border-white/40 px-7 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">Message on Facebook</a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="py-6" style={{ background: t.dark }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-center gap-8 text-center">
          {c.rating && (
            <div><div className="text-2xl md:text-3xl font-bold dental-serif" style={{ color: t.primary }}>{c.rating.value} <Star size={16} className="inline -mt-1" /></div><div className="text-xs text-white/60 mt-1">{c.rating.count} public reviews</div></div>
          )}
          {c.whatClinicRating && (
            <div><div className="text-2xl md:text-3xl font-bold dental-serif" style={{ color: t.primary }}>{c.whatClinicRating.value} <Star size={16} className="inline -mt-1" /></div><div className="text-xs text-white/60 mt-1">WhatClinic, {c.whatClinicRating.count} verified</div></div>
          )}
          {c.fbRecommend && (
            <div><div className="text-2xl md:text-3xl font-bold dental-serif" style={{ color: t.primary }}>{c.fbRecommend.pct}%</div><div className="text-xs text-white/60 mt-1">Recommend on Facebook, {c.fbRecommend.count} reviews</div></div>
          )}
          {!c.rating && c.qualitativeReputation && (
            <p className="text-sm text-white/80 max-w-md" data-testid="dental-qualitative-reputation">{c.qualitativeReputation}</p>
          )}
          {c.since && (
            <div data-testid="dental-since-badge"><div className="text-2xl md:text-3xl font-bold dental-serif" style={{ color: t.primary }}>{c.since}</div><div className="text-xs text-white/60 mt-1">Serving patients since</div></div>
          )}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>What We Treat</span>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold dental-serif">Services at {c.name}</h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
          {c.services.map((s, i) => {
            const Icon = [Stethoscope, Smile, ShieldCheck, CalendarCheck][i % 4];
            return (
              <div key={s} data-testid={`dental-service-${i}`} className="rounded-2xl bg-white border p-6 shadow-sm" style={{ borderColor: `${t.primary}30` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${t.primary}1F` }}><Icon size={20} style={{ color: t.primary }} /></div>
                <h3 className="text-base font-semibold">{s}</h3>
              </div>
            );
          })}
        </div>
        {c.doctor && <p className="mt-8 text-center text-sm" style={{ color: `${t.text}99` }}>Led by {c.doctor}</p>}
      </section>

      {/* DOCTOR SPOTLIGHT */}
      {c.doctorSpotlight && (
        <section className="py-12 md:py-16" style={{ background: `${t.primary}0F` }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 grid md:grid-cols-[auto,1fr] gap-8 items-center">
            {c.doctorSpotlight.img && <img src={c.doctorSpotlight.img} alt={c.doctorSpotlight.name} className="w-full sm:w-64 h-40 rounded-2xl object-cover shadow-md mx-auto" style={{ objectPosition: c.doctorSpotlight.imgPosition || "center" }} />}
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>Meet the Team</span>
              <h3 className="mt-1 text-2xl font-bold dental-serif">{c.doctorSpotlight.name}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: `${t.text}CC` }}>{c.doctorSpotlight.blurb}</p>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY GROUPS: spaces / team / care */}
      {[
        { key: "spaces", data: c.spaces, eyebrow: "A Closer Look", title: `Inside ${c.name}` },
        { key: "team", data: c.team, eyebrow: "Meet the Team", title: "The people behind the care" },
        { key: "care", data: c.care, eyebrow: "Care & Treatments", title: "What they treat, in real photos" },
      ].filter((group) => group.data && group.data.length > 0).map((group, gi) => (
        <section key={group.key} className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16" style={gi % 2 === 1 ? { background: `${t.primary}0A` } : undefined}>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>{group.eyebrow}</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold dental-serif">{group.title}</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {group.data.map((g) => (
              <div key={g.img} data-testid="dental-gallery-item" className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-md rounded-2xl overflow-hidden border shadow-sm flex flex-col" style={{ borderColor: `${t.primary}30`, background: t.light }}>
                <img src={g.img} alt={g.caption} className="w-full max-h-[420px] object-contain" loading="lazy" />
                <p className="px-4 py-3 text-sm mt-auto" style={{ color: `${t.text}99` }}>{g.caption}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* BRANCHES */}
      {c.branches && (
        <section className="py-12 md:py-16" style={{ background: `${t.primary}0F` }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>Locations</span>
              <h3 className="mt-1 text-2xl font-bold dental-serif">{c.branches.length === 2 ? "Two Convenient Branches" : `${c.branches.length} Locations`}</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.branches.map((b) => (
                <div key={b.name} data-testid="dental-branch" className="rounded-2xl bg-white p-6 shadow-sm border" style={{ borderColor: `${t.primary}30` }}>
                  <Building2 size={18} style={{ color: t.primary }} />
                  <h4 className="mt-2 font-semibold">{b.name}</h4>
                  <p className="mt-1 text-sm" style={{ color: `${t.text}99` }}>{b.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS / PATIENT VOICES */}
      {c.quotes && c.quotes.length > 0 && (
        <section id="reviews" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>Patient Voices</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold dental-serif">What people say publicly</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {c.quotes.map((q, i) => (
              <div key={i} data-testid={`dental-quote-${i}`} className="rounded-2xl bg-white p-6 shadow-sm border" style={{ borderColor: `${t.primary}30` }}>
                <Quote size={18} style={{ color: t.primary }} />
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${t.text}CC` }}>"{q.text}"</p>
                <p className="mt-3 text-xs" style={{ color: `${t.text}80` }}>{q.author ? `${q.author} — ` : ""}{q.source}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {(!c.quotes || c.quotes.length === 0) && (
        <section id="reviews" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center">
          <p className="text-sm" style={{ color: `${t.text}80` }}>No individually readable patient review text was reliably recoverable for {c.name} — see aggregate figures above.</p>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-16" style={{ background: `${t.primary}0F` }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>Booking a Visit</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold dental-serif">How it works</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              [Eye, "Browse", "See services and public info shared by the clinic."],
              [MessageCircle, "Message or Call", "Reach out on Facebook or by phone to ask about availability."],
              [CheckCircle2, "Confirm", "Confirm your appointment time and any treatment questions."],
              [Handshake, "Visit", `Visit ${c.name} in person for your appointment.`],
            ].map(([Icon, title, text], i) => (
              <div key={title} data-testid={`dental-step-${i}`} className="rounded-2xl bg-white p-6 shadow-sm text-center border" style={{ borderColor: `${t.primary}30` }}>
                <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `${t.primary}1F` }}><Icon size={20} style={{ color: t.primary }} /></div>
                <div className="text-xs font-bold tracking-widest" style={{ color: t.primary }}>STEP {i + 1}</div>
                <h3 className="mt-1 text-base font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm" style={{ color: `${t.text}99` }}>{text}</p>
              </div>
            ))}
          </div>
          {c.bookingLink && (
            <p className="mt-6 text-center text-sm">
              Prefers online booking? <a href={c.bookingLink} target="_blank" rel="noopener noreferrer" data-testid="dental-booking-link" className="font-semibold underline" style={{ color: t.primary }}>Book an appointment online</a>
            </p>
          )}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-12 md:py-20 text-white" style={{ background: t.dark }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: t.primary }}>Visit Us</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">Find {c.name}</h2>
            <p className="mt-4 text-sm text-white/60">Contact details below are from public listings. Please confirm current hours before visiting.</p>
            <ul className="mt-6 space-y-5">
              <li className="flex items-start gap-4">
                <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><MapPin size={20} style={{ color: t.primary }} /></span>
                <div><div className="text-xs uppercase tracking-wide text-white/50">Address</div><div className="text-white">{c.address}</div></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><Phone size={20} style={{ color: t.primary }} /></span>
                <div><div className="text-xs uppercase tracking-wide text-white/50">Phone</div>{c.phone && <a href={`tel:${c.phone.replace(/\D/g, "")}`} className="text-white hover:underline">{c.phone}</a>}{c.phone2 && <span className="block text-sm text-white/70">{c.phone2}</span>}</div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><Facebook size={20} style={{ color: t.primary }} /></span>
                <div><div className="text-xs uppercase tracking-wide text-white/50">Message on Facebook</div><a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-facebook-link" className="text-white hover:underline break-all">{c.facebook.replace("https://www.", "")}</a>{c.facebook2 && <span className="block mt-0.5"><a href={c.facebook2} target="_blank" rel="noopener noreferrer" data-testid="dental-facebook2-link" className="text-white/70 hover:underline break-all text-sm">{c.facebook2.replace("https://www.", "")}</a></span>}</div>
              </li>
              {c.instagram && (
                <li className="flex items-start gap-4">
                  <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><Instagram size={20} style={{ color: t.primary }} /></span>
                  <div><div className="text-xs uppercase tracking-wide text-white/50">Instagram</div><a href={c.instagram} target="_blank" rel="noopener noreferrer" data-testid="dental-instagram-link" className="text-white hover:underline break-all">{c.instagram.replace("https://www.", "")}</a></div>
                </li>
              )}
              {c.hours && (
                <li className="flex items-start gap-4">
                  <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><Clock size={20} style={{ color: t.primary }} /></span>
                  <div><div className="text-xs uppercase tracking-wide text-white/50">Hours</div><div className="text-white" data-testid="dental-hours">{c.hours}</div></div>
                </li>
              )}
              {c.email && (
                <li className="flex items-start gap-4">
                  <span className="rounded-xl p-3" style={{ background: `${t.primary}26` }}><Mail size={20} style={{ color: t.primary }} /></span>
                  <div><div className="text-xs uppercase tracking-wide text-white/50">Email</div><a href={`mailto:${c.email}`} className="text-white hover:underline break-all" data-testid="dental-email">{c.email}</a></div>
                </li>
              )}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden border min-h-[320px]" style={{ borderColor: `${t.primary}40` }}>
            <iframe title={`${c.name} location map`} src={`https://www.google.com/maps?q=${encodeURIComponent(c.mapQuery)}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10" style={{ background: t.dark, color: "#FFFFFF99" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <p>© {new Date().getFullYear()} {c.name}.</p>
            <p>Concept website by <a href="https://mysitecare.eu" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: t.primary }}>MySiteCare</a></p>
          </div>
          <p className="mt-4 text-[11px] text-white/40 leading-relaxed">
            This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {c.name}. Treatment, pricing, and availability should always be confirmed directly with the clinic before booking.
          </p>
        </div>
      </footer>

      <a href={c.facebook} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact"
        className="md:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: t.primary }}>
        <MessageCircle size={17} /> Message {c.name}
      </a>
      <ConceptCTAPill slug={slug} />
    </div>
  );
}
